import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Navigate, Outlet, NavLink, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { portalApi, getPortalToken, getPortalUser, clearPortalSession, formatPortalError } from "@/api/portalClient";
import { toast } from "sonner";
import {
  LogOut, User as UserIcon, Calendar, Receipt, Home, KeyRound, Loader2,
  UtensilsCrossed, Users, FileText, Calculator, UserCheck, FileSpreadsheet,
  Package, FolderOpen, Truck, Car, Wrench, Banknote, Sparkles, HeartHandshake,
  Briefcase, ListChecks, Video, Hammer, Building2, ShieldCheck, FileBarChart,
  Square, ExternalLink,
} from "lucide-react";

// Mapping icon-strings (backend) → lucid-react components
const ICONS = {
  Home, UserIcon, Calendar, Receipt, UtensilsCrossed, Users, FileText, Calculator,
  UserCheck, FileSpreadsheet, Package, FolderOpen, Truck, Car, Wrench, Banknote,
  Sparkles, HeartHandshake, Briefcase, ListChecks, Video, Hammer, Building2,
  ShieldCheck, FileBarChart, Square,
};

const STATIC_NAV = [
  { to: "/employee-portal", label: "Yleiskuva", icon: Home, end: true },
  { to: "/employee-portal/shifts", label: "Omat vuorot", icon: Calendar, payrollOnly: true },
  { to: "/employee-portal/payslips", label: "Palkkalaskelmat", icon: Receipt, payrollOnly: true },
  { to: "/employee-portal/profile", label: "Profiili", icon: UserIcon },
];

export default function EmployeePortalLayout() {
  const navigate = useNavigate();
  const [me, setMe] = useState(getPortalUser());
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    try {
      const r = await portalApi.get("/employee-portal/me");
      setMe(r.data);
      // Lataa moduulit erikseen (palauttaa tyhjän listan jos ei oikeuksia)
      try {
        const mr = await portalApi.get("/employee-portal/me/modules");
        setModules(mr.data?.modules || []);
      } catch {
        setModules([]);
      }
    } catch (e) {
      clearPortalSession();
      navigate("/employee-portal/login", { replace: true });
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  if (!getPortalToken()) return <Navigate to="/employee-portal/login" replace />;
  if (loading) return <div className="app-bg min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber-300" /></div>;

  // Pakollinen salasananvaihto
  if (me?.must_change_password && !window.location.pathname.includes("change-password")) {
    return <Navigate to="/employee-portal/change-password" replace />;
  }

  const logout = () => {
    clearPortalSession();
    toast.success("Olet kirjautunut ulos");
    navigate("/employee-portal/login", { replace: true });
  };

  const isPayrollOnly = me?.subject_type === "payroll_employee";
  const isStaffUser = me?.subject_type === "staff_user";
  const hasPayroll = me?.has_payroll_record !== false;

  // Suodata staattiset linkit: jos staff_user ilman payroll-linkitystä, älä näytä shifts/payslips
  const staticNav = STATIC_NAV.filter((it) => {
    if (it.payrollOnly && isStaffUser && !hasPayroll) return false;
    return true;
  });

  return (
    <div className="app-bg min-h-screen" data-testid="emp-portal-layout">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-white/5 backdrop-blur-xl bg-zinc-950/70">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-4">
          <Link to="/employee-portal" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 via-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
              <span className="text-zinc-900 font-display font-extrabold text-xs tracking-tight">A2G</span>
            </div>
            <div className="hidden sm:block">
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 leading-tight">Apps2Go Hub</p>
              <p className="text-xs font-display text-white leading-tight">
                {isStaffUser ? "Henkilökunnan portaali" : "Työntekijän portaali"}
              </p>
            </div>
          </Link>

          <nav className="ml-4 flex items-center gap-1 overflow-x-auto" data-testid="emp-portal-nav">
            {staticNav.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs whitespace-nowrap transition ${isActive ? "bg-amber-500/15 text-amber-200" : "text-white/60 hover:text-white hover:bg-white/5"}`
                }
                data-testid={`emp-portal-nav-${item.to.split('/').pop() || 'home'}`}
              >
                <item.icon className="w-3.5 h-3.5" /> {item.label}
              </NavLink>
            ))}
            {/* Dynaamiset moduuli-linkit (Phase 17C) */}
            {modules.length > 0 && (
              <div className="ml-2 pl-2 border-l border-white/10 flex items-center gap-1">
                {modules.slice(0, 6).map((m) => {
                  const Icon = ICONS[m.icon] || Square;
                  return (
                    <button
                      key={m.key}
                      onClick={() => navigate(m.path)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs whitespace-nowrap text-white/60 hover:text-amber-200 hover:bg-amber-500/10 transition"
                      data-testid={`emp-portal-module-${m.key}`}
                      title={`${m.label} · ${m.access_level}`}
                    >
                      <Icon className="w-3.5 h-3.5" /> {m.label}
                    </button>
                  );
                })}
                {modules.length > 6 && (
                  <NavLink
                    to="/employee-portal"
                    className="text-[10px] text-white/40 hover:text-white px-2"
                    data-testid="emp-portal-modules-more"
                  >
                    +{modules.length - 6}
                  </NavLink>
                )}
              </div>
            )}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            <div className="hidden md:flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-400 to-purple-600 flex items-center justify-center text-xs font-bold text-zinc-900">
                {(me?.first_name?.[0] || "?") + (me?.last_name?.[0] || "")}
              </div>
              <div className="text-xs">
                <p className="text-white">{me?.first_name} {me?.last_name}</p>
                <p className="text-white/40 text-[10px]">{me?.email}</p>
              </div>
            </div>
            <Button size="sm" variant="ghost" onClick={logout} className="text-white/60 hover:text-white" data-testid="emp-portal-logout">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        <Outlet context={{ me, modules, refresh: loadAll, isPayrollOnly, isStaffUser, hasPayroll }} />
      </main>
    </div>
  );
}
